"use client";
import { useRef, useEffect } from "react";

/**
 * Hook simples para detectar quando um elemento entra na tela
 * e disparar o carregamento de mais conteúdo.
 *
 * @param {Function} onLoadMore - função chamada ao atingir o final
 * @param {boolean} loading - se está carregando atualmente
 * @param {boolean} hasNextPage - se há mais páginas para carregar
 * @param {Object} options - opções do IntersectionObserver
 * @returns {React.MutableRefObject} ref - ref a ser passada ao elemento observável
 */
export default function useInfiniteScroll({ onLoadMore, loading, hasNextPage, options = {} }) {
    const targetRef = useRef(null);

    useEffect(() => {
        if (!hasNextPage || loading) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const first = entries[0];
                if (first.isIntersecting) {
                    onLoadMore();
                }
            },
            {
                rootMargin: "0px 0px 100px 0px",
                threshold: 0,
                ...options,
            }
        );

        const { current } = targetRef;
        if (current) observer.observe(current);

        return () => {
            if (current) observer.unobserve(current);
        };
    }, [loading, hasNextPage, onLoadMore]);

    return targetRef;
}
